import React, { useState } from 'react';
import Modal from '../Modal/Modal';
import { apiHelper } from '../../utils/api';
import { API_ENDPOINTS } from '../../utils/constants';
import { showToast } from '../../utils/toast';
import './SessionManagerModal.scss';

// ------------------------------------------------------------------
// Helpers
// ------------------------------------------------------------------

/** Convert a raw User-Agent string to a human-readable "Browser on OS" label */
function parseDevice(ua) {
  if (!ua || ua === 'Unknown Device') return 'Unknown Device';

  const browsers = [
    ['Edge', /Edg\//i],
    ['Opera', /OPR|Opera/i],
    ['Chrome', /Chrome/i],
    ['Firefox', /Firefox/i],
    ['Safari', /Safari/i],
  ];
  const oses = [
    ['iOS', /iPhone|iPad/i],
    ['Android', /Android/i],
    ['Windows', /Windows/i],
    ['macOS', /Mac OS X/i],
    ['Linux', /Linux/i],
  ];

  const browser = browsers.find(([, re]) => re.test(ua))?.[0] || 'Browser';
  const os = oses.find(([, re]) => re.test(ua))?.[0] || 'Device';
  return `${browser} on ${os}`;
}

/** Choose a device icon based on the parsed OS */
function deviceIcon(ua) {
  if (!ua) return '🖥️';
  if (/iPhone|iPad|iOS/i.test(ua)) return '📱';
  if (/Android/i.test(ua)) return '📱';
  if (/Windows|Mac OS X|Linux/i.test(ua)) return '💻';
  return '🖥️';
}

/** Format a date to a readable local string */
function fmt(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ------------------------------------------------------------------
// Component
// ------------------------------------------------------------------

/**
 * SessionManagerModal
 *
 * Props:
 *  - isOpen       {boolean}
 *  - onClose      {function}
 *  - sessions     {Array<{ id, device, ipAddress, createdAt, lastActive }>}
 *  - email        {string}  – user's email (from the failed login attempt)
 *  - password     {string}  – user's password (from the failed login attempt)
 *  - cleanupToken {string}  – short-lived JWT issued for Google users (no password)
 *  - onSessionRemoved {function(sessionId)} – called after a session is deleted
 */
const SessionManagerModal = ({
  isOpen,
  onClose,
  sessions = [],
  email,
  password,
  cleanupToken,
  onSessionRemoved,
}) => {
  const [loadingId, setLoadingId] = useState(null);

  const handleLogout = async (sessionId) => {
    setLoadingId(sessionId);
    try {
      const body = cleanupToken
        ? { cleanupToken, sessionId }
        : { email, password, sessionId };
      await apiHelper.delete(API_ENDPOINTS.SESSION_LOGOUT(sessionId), {
        data: body,
      });
      showToast('Session logged out.');
      onSessionRemoved(sessionId);
    } catch (err) {
      const msg =
        err.response?.data?.message || 'Failed to logout session. Please try again.';
      showToast(msg);
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Active Sessions"
      size="medium"
    >
      <div className="session-manager">
        <p className="session-manager__subtitle">
          You have reached the maximum number of active sessions. Log out from
          one of the sessions below to sign in here.
        </p>

        {sessions.length === 0 ? (
          <p className="session-manager__empty">No active sessions found.</p>
        ) : (
          <ul className="session-manager__list">
            {sessions.map((s) => (
              <li key={s.id} className="session-manager__item">
                <span className="session-manager__icon">
                  {deviceIcon(s.device)}
                </span>
                <div className="session-manager__info">
                  <span className="session-manager__device">
                    {parseDevice(s.device)}
                  </span>
                  {/* <span className="session-manager__meta">
                    <span className="session-manager__meta-label">IP:</span>{' '}
                    {s.ipAddress}
                  </span> */}
                  <span className="session-manager__meta">
                    <span className="session-manager__meta-label">Signed in:</span>{' '}
                    {fmt(s.createdAt)}
                  </span>
                  <span className="session-manager__meta">
                    <span className="session-manager__meta-label">Last active:</span>{' '}
                    {fmt(s.lastActive)}
                  </span>
                </div>
                <button
                  className="session-manager__logout-btn"
                  onClick={() => handleLogout(s.id)}
                  disabled={loadingId === s.id}
                  aria-label="Log out this session"
                >
                  {loadingId === s.id ? 'Logging out…' : 'Log Out'}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
};

export default SessionManagerModal;
